import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, deleteDoc, setDoc, query, orderBy, updateDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword, getAuth } from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import { db, auth, firebaseConfig } from '../../firebase/config';
import { Student } from '../../types';
import StudentForm from '../../components/forms/StudentForm';
import StudentView from '../../components/StudentView';
import { Button } from '../../components/ui';
import { useToast } from '../../contexts/ToastContext';

const StudentsList: React.FC = () => {
    const [students, setStudents] = useState<Student[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingStudent, setEditingStudent] = useState<Student | null>(null);
    const [formLoading, setFormLoading] = useState(false);
    const { showSuccess, showError } = useToast();
    const navigate = useNavigate();

    useEffect(() => {
        fetchStudents();
    }, []);

    const fetchStudents = async () => {
        try {
            const studentsRef = collection(db, 'students');
            const q = query(studentsRef, orderBy('createdAt', 'desc'));
            const querySnapshot = await getDocs(q);
            const studentsData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                dateOfBirth: doc.data().dateOfBirth?.toDate(),
                createdAt: doc.data().createdAt?.toDate(),
                updatedAt: doc.data().updatedAt?.toDate(),
                academicInfo: {
                    ...doc.data().academicInfo,
                    admissionDate: doc.data().academicInfo?.admissionDate?.toDate(),
                },
            })) as Student[];
            setStudents(studentsData);
        } catch (error) {
            console.error('Error fetching students:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this student?')) {
            try {
                await deleteDoc(doc(db, 'students', id));
                setStudents(students.filter(student => student.id !== id));
            } catch (error) {
                console.error('Error deleting student:', error);
            }
        }
    };

    const handleFormClose = () => {
        setShowForm(false);
        setEditingStudent(null);
    };

    const handleEditStudent = (student: Student) => {
        setEditingStudent(student);
        setShowForm(true);
    };

    const handleFormSubmit = async (studentData: Omit<Student, 'id' | 'createdAt' | 'updatedAt'>) => {
        setFormLoading(true);
        try {
            if (editingStudent) {
                // Update existing student
                const studentRef = doc(db, 'students', editingStudent.id);
                const { password, confirmPassword, ...updateData } = studentData;
                await setDoc(studentRef, {
                    ...updateData,
                    updatedAt: new Date(),
                }, { merge: true });
                showSuccess('Success', 'Student updated successfully!');
            } else {
                // Create new student with Firebase Auth
                if (!studentData.password) {
                    showError('Password Required', 'Password is required for new students.');
                    return;
                }

                // Create a separate Firebase app instance to avoid interfering with current user session
                const tempApp = initializeApp(firebaseConfig, 'temp-student-creation');
                const tempAuth = getAuth(tempApp);

                // Create Firebase Auth user using temporary auth instance
                const userCredential = await createUserWithEmailAndPassword(
                    tempAuth,
                    studentData.email,
                    studentData.password
                );

                // Create student document in Firestore using UID as document ID
                const { password, confirmPassword, ...studentDoc } = studentData;
                const studentRef = doc(db, 'students', userCredential.user.uid);
                console.log('Creating student document with ID:', userCredential.user.uid);
                await setDoc(studentRef, {
                    ...studentDoc,
                    uid: userCredential.user.uid, // Link to Firebase Auth user
                    createdAt: new Date(),
                    updatedAt: new Date(),
                });
                console.log('Student document created with ID:', studentRef.id);

                // Sign out from the temporary auth instance
                await tempAuth.signOut();

                showSuccess('Success', 'Student account has been successfully created with authentication.');
            }

            // Refresh the list and close form
            await fetchStudents();
            handleFormClose();
        } catch (error: any) {
            console.error('Error saving student:', error);
            showError('Error', error.message || 'Failed to save student. Please try again.');
        } finally {
            setFormLoading(false);
        }
    };

    const handleStatusToggle = async (studentId: string, newStatus: 'active' | 'inactive', reason?: string) => {
        try {
            const studentRef = doc(db, 'students', studentId);
            await updateDoc(studentRef, {
                'academicInfo.status': newStatus,
                'academicInfo.statusChangeReason': reason || '',
                'academicInfo.statusChangeDate': new Date(),
                updatedAt: new Date(),
            });

            showSuccess('Success', `Student status changed to ${newStatus} successfully!`);
            await fetchStudents();
        } catch (error) {
            console.error('Error updating student status:', error);
            showError('Error', 'Failed to update student status. Please try again.');
        }
    };


    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <StudentView
                students={students}
                onEdit={handleEditStudent}
                onToggleStatus={handleStatusToggle}
                onAddStudent={() => setShowForm(true)}
                loading={loading}
            />

            {/* Student Form Modal */}
            {showForm && (
                <StudentForm
                    student={editingStudent || undefined}
                    onSubmit={handleFormSubmit}
                    onCancel={handleFormClose}
                    loading={formLoading}
                />
            )}
        </div>
    );
};

const StudentDetail: React.FC = () => {
    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Student Details</h2>
                <button
                    onClick={() => window.history.back()}
                    className="btn-secondary"
                >
                    Back
                </button>
            </div>
            <div className="card">
                <p className="text-gray-600">Student detail view will be implemented here.</p>
            </div>
        </div>
    );
};

const Students: React.FC = () => {
    return (
        <Routes>
            <Route path="/" element={<StudentsList />} />
            <Route path="/new" element={<StudentsList />} />
            <Route path="/:id" element={<StudentDetail />} />
        </Routes>
    );
};

export default Students;
